import { useMemo, useState } from 'react'
import { Download, Trash2 } from 'lucide-react'
import { Button } from '../Button'
import { Drawer } from '../Drawer'
import { Toggle } from '../Toggle'
import { toast } from '../Toast'
import { ViewToggle } from '../ViewToggle'
import type { ExportColumn, ExportMetadataEntry } from './csv'
import { downloadCsv } from './csv'
import { downloadXlsx } from './xlsx'
import {
  addTemplate,
  loadTemplates,
  removeTemplate,
  type ExportTemplate,
} from './export-templates'

type ExportFormat = 'csv' | 'xlsx'

const FORMAT_OPTIONS = [
  { value: 'csv' as const, label: 'CSV' },
  { value: 'xlsx' as const, label: 'Excel' },
]

export interface ExportDrawerProps<TRow> {
  open: boolean
  onOpenChange: (open: boolean) => void
  rows: readonly TRow[]
  columns: readonly ExportColumn<TRow>[]
  /** Module identity for template persistence + future analytics.
   *  Storage key resolves to `spi-ops:{moduleId}:export-templates`. */
  moduleId: string
  /** Filename stem; the format's extension + `-YYYY-MM-DD` is appended at
   *  download time. Example: `'spi-open-orders'` →
   *  `spi-open-orders-2026-05-14.csv` or `...xlsx`. */
  filenameBase: string
  /** Optional title override; defaults to "Export". */
  title?: string
  /** Leading label/value lines (e.g. report period, unit) stamped before
   *  the header + data rows in both formats. Optional — most callers
   *  don't need it; static across the drawer's open lifetime (rebuilt by
   *  the caller from live state on every render, same as `rows`). */
  metadata?: readonly ExportMetadataEntry[]
}

/**
 * Column-picker + saved-templates drawer for client-side CSV export.
 * Generic over `TRow`; consumers pass their own `columns` + `rows`. The
 * drawer never reaches into module state. No backend.
 *
 * Templates persist per `moduleId` in localStorage. Keys that no longer
 * exist in `columns` are filtered at load so an old template doesn't
 * blow up when the schema moves forward.
 */
export function ExportDrawer<TRow>({
  open,
  onOpenChange,
  rows,
  columns,
  moduleId,
  filenameBase,
  title = 'Export',
  metadata,
}: ExportDrawerProps<TRow>) {
  const allKeys = useMemo(() => columns.map((c) => c.key), [columns])

  const [format, setFormat] = useState<ExportFormat>('csv')
  const [checked, setChecked] = useState<Set<string>>(() => new Set(allKeys))
  const [templates, setTemplates] = useState<ExportTemplate[]>(() =>
    loadTemplates(moduleId, allKeys),
  )

  const exportKeys = useMemo(
    () => allKeys.filter((k) => checked.has(k)),
    [allKeys, checked],
  )

  function toggleKey(key: string) {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function selectAll() {
    setChecked(new Set(allKeys))
  }

  function selectNone() {
    setChecked(new Set())
  }

  function applyTemplate(t: ExportTemplate) {
    setChecked(new Set(t.columns))
  }

  function saveAsTemplate() {
    if (exportKeys.length === 0) {
      toast.warning('Pick at least one column before saving a template.')
      return
    }
    const label = window.prompt(
      'Name this column template',
      `Template ${new Date().toLocaleDateString()}`,
    )
    if (!label) return
    setTemplates(addTemplate(moduleId, allKeys, label.trim(), exportKeys))
    toast.success(`Saved "${label.trim()}"`)
  }

  function deleteTemplate(id: string, label: string) {
    setTemplates(removeTemplate(moduleId, allKeys, id))
    toast.success(`Removed "${label}"`)
  }

  function handleExport() {
    if (rows.length === 0) {
      toast.warning('No rows to export — adjust filters first.')
      return
    }
    if (exportKeys.length === 0) {
      toast.warning('Pick at least one column.')
      return
    }
    if (format === 'xlsx') {
      downloadXlsx(rows, columns, { filenameBase, columnKeys: exportKeys, metadata })
    } else {
      downloadCsv(rows, columns, { filenameBase, columnKeys: exportKeys, metadata })
    }
    toast.success(
      `Exported ${rows.length} row${rows.length === 1 ? '' : 's'} · ${exportKeys.length} column${exportKeys.length === 1 ? '' : 's'}`,
    )
    onOpenChange(false)
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      meta={
        <span>
          {rows.length.toLocaleString()} row{rows.length === 1 ? '' : 's'} ·{' '}
          {exportKeys.length} of {allKeys.length} columns
        </span>
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={saveAsTemplate}
            disabled={exportKeys.length === 0}
          >
            Save as template…
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={rows.length === 0 || exportKeys.length === 0}
            >
              <Download size={14} /> Export
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-5 p-4">
        <section className="flex flex-col gap-1.5">
          <span className="text-role-section-label">Format</span>
          <ViewToggle<ExportFormat>
            aria-label="Export format"
            value={format}
            onChange={setFormat}
            options={FORMAT_OPTIONS}
          />
        </section>

        {metadata?.length ? (
          <section>
            <header className="mb-2 border-b border-border-subtle pb-1">
              <span className="text-role-section-label">Included in the file</span>
            </header>
            <dl className="flex flex-col gap-0.5">
              {metadata.map((m) => (
                <div key={m.label} className="flex gap-2 text-2xs">
                  <dt className="text-tertiary">{m.label}:</dt>
                  <dd className="text-secondary">{m.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        {templates.length > 0 ? (
          <section>
            <header className="mb-2 flex items-center justify-between border-b border-border-subtle pb-1">
              <span className="text-role-section-label">Saved templates</span>
              <span className="text-2xs text-tertiary">
                {templates.length} saved
              </span>
            </header>
            <ul className="flex flex-col gap-1">
              {templates.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center gap-2 rounded-xs border border-border-subtle bg-surface px-2 py-1.5 hover:bg-surface-hover"
                >
                  <button
                    type="button"
                    onClick={() => applyTemplate(t)}
                    className="min-w-0 flex-1 cursor-pointer border-none bg-transparent p-0 text-left text-sm text-link hover:underline"
                    title={`Apply "${t.label}" — ${t.columns.length} columns`}
                  >
                    {t.label}
                  </button>
                  <span className="text-2xs text-tertiary">
                    {t.columns.length} col{t.columns.length === 1 ? '' : 's'}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${t.label}`}
                    onClick={() => deleteTemplate(t.id, t.label)}
                    className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section>
          <header className="mb-2 flex items-center justify-between border-b border-border-subtle pb-1">
            <span className="text-role-section-label">Columns</span>
            <div className="flex items-center gap-2 text-2xs">
              <button
                type="button"
                onClick={selectAll}
                className="cursor-pointer border-none bg-transparent p-0 text-link hover:underline"
              >
                All
              </button>
              <span className="text-tertiary">·</span>
              <button
                type="button"
                onClick={selectNone}
                className="cursor-pointer border-none bg-transparent p-0 text-link hover:underline"
              >
                None
              </button>
            </div>
          </header>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {columns.map((c) => (
              <Toggle
                key={c.key}
                checked={checked.has(c.key)}
                onChange={() => toggleKey(c.key)}
                label={c.header}
              />
            ))}
          </div>
        </section>
      </div>
    </Drawer>
  )
}
